import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext({
    theme: 'light',
    setTheme: () => {},
    toggle: () => {},
});

function resolveTheme(pref) {
    if (pref === 'light' || pref === 'dark') return pref;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
}

export function ThemeProvider({ preference = 'system', onPreferenceChange, children }) {
    const [theme, setThemeState] = useState(() => resolveTheme(preference));

    useEffect(() => {
        setThemeState(resolveTheme(preference));
    }, [preference]);

    useEffect(() => {
        if (preference !== 'system') return undefined;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => setThemeState(resolveTheme('system'));
        mq.addEventListener?.('change', handler);
        return () => mq.removeEventListener?.('change', handler);
    }, [preference]);

    const value = useMemo(
        () => ({
            theme,
            preference,
            setTheme: (next) => onPreferenceChange?.(next),
            toggle: () => onPreferenceChange?.(theme === 'dark' ? 'light' : 'dark'),
        }),
        [theme, preference, onPreferenceChange]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    return useContext(ThemeContext);
}
